import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

/**
 * R5: Fetches all notes for a project scope (newest first).
 *
 * @param projectId - Project ID the scope belongs to
 * @param scopeId - Scope ID to fetch notes for
 * @param options.enabled - Whether to fetch (default: true when both IDs are present)
 */
export function useScopeNotes(
  projectId: string | undefined,
  scopeId: string | undefined,
  options?: { enabled?: boolean }
) {
  const enabled = options?.enabled !== false && !!projectId && !!scopeId;

  const query = useQuery({
    queryKey: ['scope-notes', projectId, scopeId],
    queryFn: async () => {
      const response = await api.scopeNotes.list(projectId!, scopeId!);
      return response;
    },
    enabled,
    staleTime: 30_000,
  });

  return {
    notes: query.data?.notes ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * R5: Adds a note to a project scope.
 *
 * Cache invalidation: Invalidates scope-notes and project-scopes queries so
 * the latest_note_* fields in the compact card are refreshed.
 *
 * @example
 * const { addNote, isAdding } = useAddScopeNote();
 * await addNote({ projectId, scopeId, noteText: 'Awaiting permit sign-off' });
 */
export function useAddScopeNote() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (vars: { projectId: string; scopeId: string; noteText: string }) =>
      api.scopeNotes.create(vars.projectId, vars.scopeId, vars.noteText),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['scope-notes', variables.projectId, variables.scopeId] });
      // Refresh the scopes list so latest_note_* fields update in compact view
      queryClient.invalidateQueries({ queryKey: ['project-scopes', variables.projectId] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add note');
    },
  });

  return {
    addNote: mutation.mutateAsync,
    isAdding: mutation.isPending,
  };
}
